import React from 'react';

function AnswerKeyModal({ generatedData, onClose }) {
  const generateAnswerKeyDocx = async () => {
    // Handle both possible API response formats
    const examData = generatedData?.exam_paper || generatedData?.exam_paper_json;
    if (!examData) return;

    const { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType } = await import('docx');
    
    const doc = new Document({
      sections: [{
        properties: {},
        children: [
          // Header
          new Paragraph({
            text: `${examData.meta.subject} - Answer Key`,
            heading: HeadingLevel.HEADING_1,
            alignment: AlignmentType.CENTER,
            spacing: { after: 300 }
          }),
          new Paragraph({
            text: examData.meta.chapter_title,
            heading: HeadingLevel.HEADING_2,
            alignment: AlignmentType.CENTER,
            spacing: { after: 300 }
          }),
          new Paragraph({
            text: `Total Marks: ${examData.meta.total_marks}`,
            alignment: AlignmentType.CENTER,
            spacing: { after: 400 }
          }),

          // Section A - VSAQ Answers
          new Paragraph({
            text: 'Section A - Very Short Answer Questions',
            heading: HeadingLevel.HEADING_3,
            spacing: { before: 400, after: 200 }
          }),
          ...(examData.paper.section_a_vsaq?.flatMap((q, i) => [
            new Paragraph({
              children: [
                new TextRun({
                  text: `${i + 1}. ${q.question}`,
                  bold: true
                }),
                new TextRun({
                  text: ` (${q.marks} mark${q.marks > 1 ? 's' : ''})`
                })
              ],
              indent: { left: 200 },
              spacing: { after: 100 }
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: 'Answer: ',
                  bold: true
                }),
                new TextRun({
                  text: examData.answer_key.vsaq[i]?.answer || 'Answer not available'
                })
              ],
              indent: { left: 400 },
              spacing: { after: 50 }
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: 'Reference: ',
                  bold: true,
                  italics: true
                }),
                new TextRun({
                  text: q.reference,
                  italics: true
                })
              ],
              indent: { left: 400 },
              spacing: { after: 200 }
            })
          ]) || []),

          // Section B - SAQ Answers
          new Paragraph({
            text: 'Section B - Short Answer Questions',
            heading: HeadingLevel.HEADING_3,
            spacing: { before: 400, after: 200 }
          }),
          ...(examData.paper.section_b_saq?.flatMap((q, i) => [
            new Paragraph({
              children: [
                new TextRun({
                  text: `${i + 1}. ${q.question}`,
                  bold: true
                }),
                new TextRun({
                  text: ` (${q.marks} marks)`
                })
              ],
              indent: { left: 200 },
              spacing: { after: 100 }
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: 'Answer:',
                  bold: true
                })
              ],
              indent: { left: 400 },
              spacing: { after: 50 }
            }),
            ...(examData.answer_key.saq[i]?.points?.map(point => 
              new Paragraph({
                text: `• ${point.text} (${point.marks} mark${point.marks > 1 ? 's' : ''})`,
                indent: { left: 600 },
                spacing: { after: 50 }
              })
            ) || []),
            new Paragraph({
              children: [
                new TextRun({
                  text: 'Reference: ',
                  bold: true,
                  italics: true
                }),
                new TextRun({
                  text: q.reference,
                  italics: true
                })
              ],
              indent: { left: 400 },
              spacing: { after: 200 }
            })
          ]) || []),

          // Section C - LAQ Answers
          new Paragraph({
            text: 'Section C - Long Answer Questions',
            heading: HeadingLevel.HEADING_3,
            spacing: { before: 400, after: 200 }
          }),
          ...(examData.paper.section_c_laq?.flatMap((q, i) => [
            new Paragraph({
              children: [
                new TextRun({
                  text: `${i + 1}. ${q.question}`,
                  bold: true
                }),
                new TextRun({
                  text: ` (${q.marks} marks)`
                })
              ],
              indent: { left: 200 },
              spacing: { after: 100 }
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: 'Answer:',
                  bold: true
                })
              ],
              indent: { left: 400 },
              spacing: { after: 50 }
            }),
            // Introduction
            ...(examData.answer_key.laq?.intro ? [
              new Paragraph({
                children: [
                  new TextRun({
                    text: 'Introduction: ',
                    bold: true
                  }),
                  new TextRun({
                    text: `${examData.answer_key.laq.intro.text} (${examData.answer_key.laq.intro.marks} marks)`
                  })
                ],
                indent: { left: 600 },
                spacing: { after: 100 }
              })
            ] : []),
            // Main Points Header
            new Paragraph({
              children: [
                new TextRun({
                  text: 'Main Points:',
                  bold: true
                })
              ],
              indent: { left: 600 },
              spacing: { after: 50 }
            }),
            ...(examData.answer_key.laq?.body?.map(point => 
              new Paragraph({
                text: `• ${point.text} (${point.marks} marks)`,
                indent: { left: 800 },
                spacing: { after: 50 }
              })
            ) || []),
            // Conclusion
            ...(examData.answer_key.laq?.conclusion ? [
              new Paragraph({
                children: [
                  new TextRun({
                    text: 'Conclusion: ',
                    bold: true
                  }),
                  new TextRun({
                    text: `${examData.answer_key.laq.conclusion.text} (${examData.answer_key.laq.conclusion.marks} marks)`
                  })
                ],
                indent: { left: 600 },
                spacing: { after: 100 }
              })
            ] : []),
            new Paragraph({
              children: [
                new TextRun({
                  text: 'Reference: ',
                  bold: true,
                  italics: true
                }),
                new TextRun({
                  text: examData.answer_key.laq?.reference || q.reference,
                  italics: true
                })
              ],
              indent: { left: 400 },
              spacing: { after: 200 }
            })
          ]) || []),

          // Section D - MCQ Answers
          new Paragraph({
            text: 'Section D - Multiple Choice Questions',
            heading: HeadingLevel.HEADING_3,
            spacing: { before: 400, after: 200 }
          }),
          ...(examData.paper.section_d_mcq?.flatMap((q, i) => [
            new Paragraph({
              children: [
                new TextRun({
                  text: `${i + 1}. ${q.question}`,
                  bold: true
                })
              ],
              indent: { left: 200 },
              spacing: { after: 100 }
            }),
            ...Object.entries(q.options).map(([key, value]) => {
              const isCorrect = key === q.answer;
              return new Paragraph({
                children: [
                  new TextRun({
                    text: `   ${key}) ${value}`,
                    bold: isCorrect
                  }),
                  ...(isCorrect ? [
                    new TextRun({
                      text: ' ✓ (Correct Answer)',
                      bold: true,
                      color: '008000'
                    })
                  ] : [])
                ],
                indent: { left: 400 },
                spacing: { after: 50 }
              });
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: 'Reference: ',
                  bold: true,
                  italics: true
                }),
                new TextRun({
                  text: q.reference,
                  italics: true
                })
              ],
              indent: { left: 400 },
              spacing: { after: 200 }
            })
          ]) || [])
        ]
      }]
    });

    // Generate the document and trigger download
    const blob = await Packer.toBlob(doc);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${examData.meta.subject}_${examData.meta.chapter_title.replace(/\s+/g, '_')}_Answer_Key.docx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white p-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">Answer Key</h2>
          <div className="flex items-center space-x-4">
            <button
              onClick={generateAnswerKeyDocx}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2"
            >
              <span>📄</span> Download Answer Key
            </button>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 p-1"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        <div className="p-6">
          <div className="prose max-w-none">
            <AnswerKeyContent generatedData={generatedData} />
          </div>
        </div>
      </div>
    </div>
  );
}

const AnswerKeyContent = ({ generatedData }) => {
  // Handle both possible API response formats  
  const examData = generatedData?.exam_paper || generatedData?.exam_paper_json;
  if (!examData) {
    return <div className="text-center p-8 text-red-500">Invalid answer key data format</div>;
  }

  const { meta, paper, answer_key } = examData;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold mb-2">{meta.subject} - Answer Key</h1>
        <h2 className="text-xl font-semibold mb-4">{meta.chapter_title}</h2>
        <div className="text-center mb-4">
          <span className="font-semibold">Total Marks: {meta.total_marks}</span>
        </div>
      </div>

      {/* Section A - VSAQ Answers */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-4 text-blue-700 border-b-2 border-blue-200 pb-2">
          Section A - Very Short Answer Questions
        </h3>
        
        <div className="space-y-6">
          {paper.section_a_vsaq?.map((question, index) => (
            <div key={index} className="bg-gray-50 p-4 rounded-lg">
              <div className="mb-2">
                <span className="font-bold text-gray-800">{index + 1}. {question.question}</span>
                <span className="ml-2 text-sm text-blue-600 font-medium">({question.marks} mark{question.marks > 1 ? 's' : ''})</span>
              </div>
              <div className="ml-4 space-y-1">
                <div>
                  <span className="font-semibold text-green-700">Answer: </span>
                  <span className="text-gray-800">{answer_key.vsaq[index]?.answer || 'Answer not available'}</span>
                </div>
                <div>
                  <span className="font-semibold text-blue-600 italic">Reference: </span>
                  <span className="text-gray-600 italic text-sm">{question.reference}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Section B - SAQ Answers */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-4 text-blue-700 border-b-2 border-blue-200 pb-2">
          Section B - Short Answer Questions
        </h3>
        
        <div className="space-y-6">
          {paper.section_b_saq?.map((question, index) => (
            <div key={index} className="bg-gray-50 p-4 rounded-lg">
              <div className="mb-3">
                <span className="font-bold text-gray-800">{index + 1}. {question.question}</span>
                <span className="ml-2 text-sm text-blue-600 font-medium">({question.marks} marks)</span>
              </div>
              <div className="ml-4 space-y-2">
                <div className="font-semibold text-green-700">Answer:</div>
                <div className="ml-4 space-y-1">
                  {answer_key.saq[index]?.points?.map((point, pointIndex) => (
                    <div key={pointIndex} className="flex items-start">
                      <span className="text-green-600 mr-2">•</span>
                      <span className="text-gray-800">{point.text}</span>
                      <span className="ml-2 text-sm text-blue-600 font-medium">({point.marks} mark{point.marks > 1 ? 's' : ''})</span>
                    </div>
                  )) || <span className="text-gray-500 ml-4">Answer points not available</span>}
                </div>
                <div className="mt-2">
                  <span className="font-semibold text-blue-600 italic">Reference: </span>
                  <span className="text-gray-600 italic text-sm">{question.reference}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Section C - LAQ Answers */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-4 text-blue-700 border-b-2 border-blue-200 pb-2">
          Section C - Long Answer Questions
        </h3>
        
        <div className="space-y-6">
          {paper.section_c_laq?.map((question, index) => (
            <div key={index} className="bg-gray-50 p-4 rounded-lg">
              <div className="mb-3">
                <span className="font-bold text-gray-800">{index + 1}. {question.question}</span>
                <span className="ml-2 text-sm text-blue-600 font-medium">({question.marks} marks)</span>
              </div>
              <div className="ml-4 space-y-3">
                <div className="font-semibold text-green-700">Answer:</div>
                
                {/* Introduction */}
                {answer_key.laq?.intro && (
                  <div className="ml-4">
                    <div className="font-semibold text-purple-700">Introduction:</div>
                    <div className="ml-4 text-gray-800">
                      {answer_key.laq.intro.text}
                      <span className="ml-2 text-sm text-blue-600 font-medium">({answer_key.laq.intro.marks} marks)</span>
                    </div>
                  </div>
                )}

                {/* Main Points */}
                <div className="ml-4">
                  <div className="font-semibold text-purple-700 mb-2">Main Points:</div>
                  <div className="ml-4 space-y-1">
                    {answer_key.laq?.body?.map((point, pointIndex) => (
                      <div key={pointIndex} className="flex items-start">
                        <span className="text-green-600 mr-2">•</span>
                        <span className="text-gray-800">{point.text}</span>
                        <span className="ml-2 text-sm text-blue-600 font-medium">({point.marks} marks)</span>
                      </div>
                    )) || <span className="text-gray-500 ml-4">Main points not available</span>}
                  </div>
                </div>

                {/* Conclusion */}
                {answer_key.laq?.conclusion && (
                  <div className="ml-4">
                    <div className="font-semibold text-purple-700">Conclusion:</div>
                    <div className="ml-4 text-gray-800">
                      {answer_key.laq.conclusion.text}
                      <span className="ml-2 text-sm text-blue-600 font-medium">({answer_key.laq.conclusion.marks} marks)</span>
                    </div>
                  </div>
                )}

                <div className="mt-3">
                  <span className="font-semibold text-blue-600 italic">Reference: </span>
                  <span className="text-gray-600 italic text-sm">{answer_key.laq?.reference || question.reference}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Section D - MCQ Answers */}
      <div>
        <h3 className="text-lg font-semibold mb-4 text-blue-700 border-b-2 border-blue-200 pb-2">
          Section D - Multiple Choice Questions
        </h3>
        
        <div className="space-y-6">
          {paper.section_d_mcq?.map((question, index) => (
            <div key={index} className="bg-gray-50 p-4 rounded-lg">
              <div className="mb-3">
                <span className="font-bold text-gray-800">{index + 1}. {question.question}</span>
              </div>
              <div className="ml-4 space-y-1 mb-3">
                {Object.entries(question.options).map(([key, value]) => {
                  const isCorrect = key === question.answer;
                  return (
                    <div key={key} className={`flex items-center ${isCorrect ? 'bg-green-100 border-l-4 border-green-500 pl-2 py-1' : ''}`}>
                      <span className={`font-mono mr-2 ${isCorrect ? 'font-bold text-green-700' : 'text-gray-600'}`}>
                        {key})
                      </span>
                      <span className={isCorrect ? 'font-semibold text-green-800' : 'text-gray-700'}>
                        {value}
                      </span>
                      {isCorrect && (
                        <span className="ml-2 text-green-600 font-bold">✓ (Correct Answer)</span>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="ml-4">
                <span className="font-semibold text-blue-600 italic">Reference: </span>
                <span className="text-gray-600 italic text-sm">{question.reference}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AnswerKeyModal;